import Container from '@components/container';
import Layout from '@components/layout';
import Sections from '@components/sections';
import ImageSection from '@components/sections/image';
import { Seo } from '@components/seo';
import {
  EventsJsonLd,
  NewsArticleJsonLd,
  WebPageJsonLd,
} from '@components/structured-data';
import { getNews } from '@lib/cms-client';
import { parseDate } from '@lib/date';
import { ContainerSize, Menu, News, Settings } from '@lib/types';
import { GetServerSidePropsContext } from 'next';

type Props = {
  isPreview: boolean;
  menu: Menu;
  menuFooter: Menu;
  menuFooterSecondary: Menu;
  settings: Settings;
  news: News;
  url: string;
};

export default function NewsPage({
  isPreview,
  news,
  menu,
  menuFooter,
  menuFooterSecondary,
  settings,
  url,
}: Props) {
  return (
    <Layout
      isPreview={isPreview}
      menu={menu}
      menuFooter={menuFooter}
      menuFooterSecondary={menuFooterSecondary}
      settings={settings}>
      <Seo title={news.title} url={url} seo={news.seo} />
      {settings.website && (
        <>
          <WebPageJsonLd
            url={url}
            title={news.title}
            locale={news.locale ?? undefined}
            seo={news.seo}
            website={settings.website}
          />
          <NewsArticleJsonLd
            news={news}
            url={url}
            website={settings.website}
          />
        </>
      )}
      <EventsJsonLd sections={news.content} />
      <Container size={ContainerSize.wide}>
        <div className="grid grid-cols-12 gap-10">
          <h1 className="text-32 md:text-35 leading-130 font-bold text-blue-800 col-start-2 col-span-11 lg:col-start-4 lg:col-span-6">
            {news.title}
          </h1>
          <p className="text-17 leading-140 md:text-20 md:leading-124 text-iceblue-500 font-bold col-start-2 col-span-11 lg:col-start-4 lg:col-span-6">
            {parseDate(news.publishDate)}
          </p>
          {news.intro && (
            <p className="col-start-2 col-span-11 lg:col-start-4 lg:col-span-6 prose">
              {news.intro}
            </p>
          )}
        </div>
      </Container>
      <ImageSection data={{ image: news.image }} />
      <Sections sections={news.content} settings={settings} />
    </Layout>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const slug = context.query.slug as string[];
  const url = new URL(
    context.resolvedUrl,
    `http://${context.req.headers.host}`,
  ).toString();

  /*
   * If news should be shown in the current locale you may also want to adjust
   * the language redirect logic. See `connectedNews()` in `lib/cms-client.ts`.
   */
  const news = await getNews(slug, Boolean(context.preview), 'en');

  if (!news) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      url,
      news,
    },
  };
}
